'use client'
import {
  createContext,
  useContext,
  useState,
  useCallback,
  FC,
  ReactNode,
} from 'react'

interface StepApi {
  step: number
  goForward(): void
  goBack(): void
  goToStep(target: number): void
  reset(): void
}

const OnboardingStepContext = createContext<StepApi | undefined>(undefined)

export const OnboardingStepProvider: FC<{
  initialStep?: number
  maxStep?: number
  children: ReactNode
}> = ({ initialStep = 0, maxStep, children }) => {
  const [step, setStep] = useState(initialStep)
  const goForward = useCallback(
    () =>
      setStep(s => {
        if (typeof maxStep === 'number') {
          return Math.min(s + 1, maxStep)
        }
        return s + 1
      }),
    [maxStep],
  )
  const goBack = useCallback(
    () =>
      setStep(s => {
        const next = Math.max(0, s - 1)

        return next
      }),
    [],
  )
  const goToStep = useCallback(
    (target: number) => {
      setStep(() => {
        const clamped = Math.max(
          0,
          typeof maxStep === 'number' ? Math.min(target, maxStep) : target,
        )
        return clamped
      })
    },
    [maxStep],
  )
  const reset = useCallback(() => setStep(initialStep), [initialStep])
  return (
    <OnboardingStepContext.Provider
      value={{ step, goForward, goBack, goToStep, reset }}
    >
      {children}
    </OnboardingStepContext.Provider>
  )
}

export function useOnboardingSteps(): StepApi {
  const ctx = useContext(OnboardingStepContext)
  if (!ctx)
    throw new Error(
      'useOnboardingSteps must be used within OnboardingStepProvider',
    )
  return ctx
}
